import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import { alpha, useTheme } from "@mui/material/styles";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import MemoryIcon from "@mui/icons-material/Memory";
import GroupsIcon from "@mui/icons-material/Groups";
import { GetDeviceHostsReportCall } from "../redux/api/report.api";

function HostRow({ label, count, percentOfTotal, maxPercent }) {
  const theme = useTheme();
  const bar =
    maxPercent > 0 ? Math.min(100, Math.round((percentOfTotal / maxPercent) * 100)) : 0;
  return (
    <Box sx={{ mb: 1.25 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1} sx={{ mb: 0.35 }}>
        <Typography
          variant="caption"
          color="text.primary"
          sx={{
            fontWeight: 500,
            fontSize: "0.8rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "72%",
          }}
          title={label}
        >
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
          {count} ({percentOfTotal}%)
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={bar}
        sx={{
          height: 6,
          borderRadius: 999,
          bgcolor:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.common.white, 0.1)
              : alpha(theme.palette.common.black, 0.06),
          "& .MuiLinearProgress-bar": { borderRadius: 999 },
        }}
      />
    </Box>
  );
}

export default function AdminStatsDeviceHosts({ embedded = false }) {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const res = await GetDeviceHostsReportCall();
      if (!cancelled) {
        setData(res && typeof res === "object" && res.totalUsers != null ? res : null);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ mb: 1 }}>
        <Skeleton variant="rounded" height={160} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (!data) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Could not load device insights.
      </Typography>
    );
  }

  const osList = Array.isArray(data.osVersions) ? data.osVersions : [];
  const deviceList = Array.isArray(data.deviceModels) ? data.deviceModels : [];
  const maxOs = Math.max(0, ...osList.map((r) => Number(r.percentOfTotal || 0)));
  const maxDev = Math.max(0, ...deviceList.map((r) => Number(r.percentOfTotal || 0)));

  return (
    <Box sx={{ mb: embedded ? 0 : 4 }}>
      {!embedded ? (
        <>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
            Client devices & hosts
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Reported from the mobile app (last known OS version and device model per user). Web-only users
            often appear as &quot;not reported&quot;.
          </Typography>
        </>
      ) : null}

      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <Chip
          icon={<GroupsIcon />}
          label={`${data.totalUsers} users`}
          variant="outlined"
          size="small"
        />
        <Chip
          icon={<MemoryIcon />}
          label={`${data.withOsVersion} with OS`}
          variant="outlined"
          size="small"
          color="primary"
        />
        <Chip
          icon={<SmartphoneIcon />}
          label={`${data.withDeviceModel} with device`}
          variant="outlined"
          size="small"
          color="secondary"
        />
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: 2,
              height: "100%",
              borderColor: alpha(theme.palette.divider, 0.9),
            }}
          >
            <CardHeader
              title="OS versions"
              subheader="Share of all users"
              titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }}
              subheaderTypographyProps={{ variant: "caption" }}
            />
            <CardContent sx={{ pt: 0, maxHeight: 420, overflow: "auto" }}>
              {osList.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No data
                </Typography>
              ) : (
                osList.map((row) => (
                  <HostRow
                    key={row.label}
                    label={row.label}
                    count={row.count}
                    percentOfTotal={row.percentOfTotal}
                    maxPercent={maxOs || 1}
                  />
                ))
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: 2,
              height: "100%",
              borderColor: alpha(theme.palette.divider, 0.9),
            }}
          >
            <CardHeader
              title="Device models"
              subheader="Top reported handsets / models"
              titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }}
              subheaderTypographyProps={{ variant: "caption" }}
            />
            <CardContent sx={{ pt: 0, maxHeight: 420, overflow: "auto" }}>
              {deviceList.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No data
                </Typography>
              ) : (
                deviceList.map((row) => (
                  <HostRow
                    key={row.label}
                    label={row.label}
                    count={row.count}
                    percentOfTotal={row.percentOfTotal}
                    maxPercent={maxDev || 1}
                  />
                ))
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
